import React, { useEffect, useRef, useState } from "react";
import { MessageCircle, X, Send, Loader2 } from "lucide-react";
import { useLang } from "@/contexts/LangContext";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const SESSION_KEY = "rh_chat_session_id";

function getSessionId() {
  let id = localStorage.getItem(SESSION_KEY);
  if (!id) {
    id = `sess-${crypto.randomUUID()}`;
    localStorage.setItem(SESSION_KEY, id);
  }
  return id;
}

export default function ChatWidget() {
  const { t, lang } = useLang();
  const [open, setOpen] = useState(false);
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState("");
  const [streaming, setStreaming] = useState(false);
  const [loadedHistory, setLoadedHistory] = useState(false);
  const scrollRef = useRef(null);
  const sessionId = useRef(getSessionId());

  useEffect(() => {
    if (!open || loadedHistory) return;
    setLoadedHistory(true);
    fetch(`${API}/chat/history/${sessionId.current}`)
      .then((r) => r.json())
      .then((data) => {
        if (Array.isArray(data) && data.length > 0) {
          setMessages(data.map((m) => ({ role: m.role, content: m.content })));
        } else {
          setMessages([{ role: "assistant", content: t("chat.greeting") }]);
        }
      })
      .catch(() => setMessages([{ role: "assistant", content: t("chat.greeting") }]));
    // Only load once when opened for the first time.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  useEffect(() => {
    if (scrollRef.current) {
      scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
    }
  }, [messages, streaming]);

  const send = async (overrideText) => {
    const text = (overrideText ?? input).trim();
    if (!text || streaming) return;
    setInput("");
    setMessages((m) => [...m, { role: "user", content: text }, { role: "assistant", content: "" }]);
    setStreaming(true);

    try {
      const res = await fetch(`${API}/chat/message`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ session_id: sessionId.current, message: text, lang }),
      });
      if (!res.ok || !res.body) throw new Error("Chat request failed");
      const reader = res.body.getReader();
      const decoder = new TextDecoder();
      let acc = "";
      // eslint-disable-next-line no-constant-condition
      while (true) {
        const { done, value } = await reader.read();
        if (done) break;
        acc += decoder.decode(value, { stream: true });
        setMessages((m) => {
          const next = [...m];
          next[next.length - 1] = { role: "assistant", content: acc };
          return next;
        });
      }
    } catch (err) {
      console.warn("Chat send failed:", err?.message);
      setMessages((m) => {
        const next = [...m];
        next[next.length - 1] = { role: "assistant", content: t("chat.error") };
        return next;
      });
    } finally {
      setStreaming(false);
    }
  };

  const handleKeyDown = (e) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      send();
    }
  };

  return (
    <>
      {open && (
        <div
          data-testid="chat-panel"
          className="fixed bottom-24 right-4 md:right-8 z-50 w-[92vw] max-w-sm h-[65vh] max-h-[560px] bg-[#0A2244] border border-white/15 shadow-2xl flex flex-col"
        >
          <div className="flex items-center justify-between px-5 py-4 border-b border-white/10">
            <div>
              <p className="font-display text-sm font-medium tracking-tight text-white">{t("chat.title")}</p>
              <p className="text-[11px] text-white/50">{t("chat.subtitle")}</p>
            </div>
            <button
              onClick={() => setOpen(false)}
              data-testid="chat-close-btn"
              className="w-8 h-8 flex items-center justify-center border border-white/15 hover:border-white transition-colors"
              aria-label={t("chat.close")}
            >
              <X size={14} strokeWidth={1.5} />
            </button>
          </div>

          <div ref={scrollRef} data-testid="chat-messages" className="flex-1 overflow-y-auto px-5 py-4 space-y-4">
            {messages.map((m, i) => (
              <div
                key={i}
                data-testid={`chat-message-${m.role}-${i}`}
                className={`text-sm leading-relaxed whitespace-pre-line ${
                  m.role === "user"
                    ? "text-white bg-white/10 px-4 py-2.5 ml-auto max-w-[85%] w-fit"
                    : "text-white/80 max-w-[95%]"
                }`}
              >
                {m.content || (streaming && i === messages.length - 1 ? t("chat.thinking") : "")}
              </div>
            ))}
            {messages.length <= 1 && !streaming && (
              <div data-testid="chat-suggestions" className="flex flex-col gap-2 pt-2">
                {[t("chat.suggestion_1"), t("chat.suggestion_2"), t("chat.suggestion_3")].map((s, i) => (
                  <button
                    key={i}
                    onClick={() => send(s)}
                    data-testid={`chat-suggestion-${i}`}
                    className="text-left text-xs text-white/70 border border-white/15 hover:border-white/40 hover:text-white px-3 py-2.5 transition-colors"
                  >
                    {s}
                  </button>
                ))}
              </div>
            )}
          </div>

          <div className="border-t border-white/10 p-3 flex items-end gap-2">
            <textarea
              value={input}
              onChange={(e) => setInput(e.target.value)}
              onKeyDown={handleKeyDown}
              placeholder={t("chat.placeholder")}
              rows={1}
              data-testid="chat-input"
              className="flex-1 bg-white/5 border border-white/15 px-3 py-2.5 text-sm text-white focus:border-white/50 outline-none resize-none max-h-24"
            />
            <button
              onClick={() => send()}
              disabled={streaming || !input.trim()}
              data-testid="chat-send-btn"
              className="w-10 h-10 shrink-0 bg-white text-[#051630] flex items-center justify-center hover:bg-transparent hover:text-white border border-white transition-colors disabled:opacity-40"
              aria-label={t("chat.send")}
            >
              {streaming ? <Loader2 size={16} className="animate-spin" /> : <Send size={16} strokeWidth={1.5} />}
            </button>
          </div>
        </div>
      )}

      <button
        onClick={() => setOpen((o) => !o)}
        data-testid="chat-toggle-btn"
        aria-label={t("chat.button_label")}
        className="fixed bottom-6 right-4 md:right-8 z-50 w-14 h-14 rounded-full bg-white text-[#051630] flex items-center justify-center shadow-xl hover:scale-105 transition-transform border border-white"
      >
        {open ? <X size={20} strokeWidth={1.5} /> : <MessageCircle size={22} strokeWidth={1.5} />}
      </button>
    </>
  );
}
